<template>
  <v-container class="page--home" fluid>
    <v-toolbar
      data-action="get-height"
      class="toolbar--white get-height action-bar"
      :class="{ 'action-bar--ml': !mainMenuIsOpen }"
      flat
    >
      <Breadcrumb :routes="breadcrumbItems" />
      <v-spacer />
      <MenuItemPeriod
        v-if="
          claims.report['ROLE_GET_REPORT_COSTCENTER'] &&
          claims.report['ROLE_GET_REPORT_TAXIJOBS'] &&
          claims.report['ROLE_GET_REPORT_CHART'] &&
          claims.report['ROLE_GET_REPORT_EMPLOYEE']
        "
        icon
        @change="updateFilters"
      />
    </v-toolbar>

    <div
      class="page-content"
      :class="{ 'page-content--sidebar': sidebarVisible }"
    >
      <div class="page-content__body">
        <RegistersWithoutCellPhone v-if="false" />

        <div class="pt-2">
          <Performance v-if="claims.report['ROLE_GET_REPORT_CHART']" :filters="filters" />
        </div>

        <v-layout row wrap>
          <v-flex v-if="!sidebarVisible && claims.report['ROLE_GET_REPORT_TAXIJOBS']" lg4 xs12>
            <LatestRides
              v-if="claims.report['ROLE_GET_REPORT_TAXIJOBS']"
              :min-height="boxHeight"
              :filters="filters"
              :route="{
                name: 'Reports',
                params: { type: 'corridas' }
              }"
              @getRideData="openRideDrawer"
            />
          </v-flex>

          <v-flex lg6 xs12>
            <Top5
              v-if="claims.report['ROLE_GET_REPORT_COSTCENTER']"
              :min-height="boxHeight"
              :route="{
                name: 'Reports',
                params: { type: 'centro-de-custo' }
              }"
              type="costCenters"
              :items="top5CostCenterItems"
              :status="costCenterStatus"
              @tryAgain="getCostCenters"
              @getCostCenterData="openCostCenterDrawer"
            />
          </v-flex>

          <v-flex lg6 xs12>
            <Top5
              v-if="claims.report['ROLE_GET_REPORT_EMPLOYEE']"
              :min-height="boxHeight"
              :route="{
                name: 'Reports',
                params: { type: 'colaborador' }
              }"
              type="employees"
              :items="top5EmployeeItems"
              :status="top5EmployeeStatus"
              @tryAgain="getEmployee"
              @getEmployeeData="openEmployeeDrawer"
            />
          </v-flex>
        </v-layout>
      </div>
    </div>

    <LatestRidesSidebar
      v-if="sidebarVisible"
      :filters="filters"
      @openRideDrawer="openRideDrawer"
    />

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { convertToCurrencyBRL, isObject } from 'utils/functions'
import Breadcrumb from 'components/Breadcrumb'
import Performance from 'components/home/performance'
import LatestRides from 'components/home/LatestRides'
import Top5 from 'components/home/Top5'
import MenuItemPeriod from 'components/menuItemPeriod'
import GlobalDrawer from 'components/globalDrawer'
import LatestRidesSidebar from 'components/home/LatestRidesSidebar'
import RegistersWithoutCellPhone from 'components/RegistersWithoutCellPhone'

export default {
  name: 'HomePage',
  components: {
    Breadcrumb,
    MenuItemPeriod,
    Performance,
    LatestRides,
    Top5,
    GlobalDrawer,
    LatestRidesSidebar,
    RegistersWithoutCellPhone
  },
  computed: {
    ...mapState({
      mainMenuIsOpen: state => state.mainMenu.isOpen,
      company: state => state.company.selectedCompany,
      claims: state => state.userClaims.claims
    })
  },
  data () {
    return {
      namespace: 'Views.HomePage',
      breadcrumbItems: [
        { name: 'Home', title: this.$t('Views.HomePage.title') }
      ],
      filters: {
        startDate: null,
        endDate: null
      },
      boxHeight: 260,
      statusRides: {},
      categoriesPercent: [],
      top5CostCenterItems: null,
      costCenterStatus: null,
      top5EmployeeItems: null,
      top5EmployeeStatus: null,
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      },
      sidebarVisible: false
    }
  },
  methods: {
    isValidObject (val) {
      return isObject(val)
    },

    getFilters () {
      return this.isValidObject(this.filters) && this.filters.startDate && this.filters.endDate
        ? { ...this.filters }
        : {}
    },

    openGlobalDrawer ({ visibility = false, type = '', data = {}, filters = {} }) {
      this.globalDrawer = {
        visibility,
        type,
        data,
        filters
      }
    },

    closeGlobalDrawer () {
      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }
    },

    openRideDrawer (ride) {
      this.openGlobalDrawer({
        visibility: true,
        type: 'ride',
        data: { id: ride.taxiJobId },
        filters: { ...this.getFilters() }
      })
    },

    openCostCenterDrawer (costCenter) {
      this.openGlobalDrawer({
        visibility: true,
        type: 'costCenter',
        data: {
          id: costCenter.id,
          name: costCenter.name
        },
        filters: {
          ...this.getFilters(),
          costCenterId: costCenter.id
        }
      })
    },

    openEmployeeDrawer (employee) {
      this.openGlobalDrawer({
        visibility: true,
        type: 'employee',
        data: {
          id: employee.employeeId,
          name: employee.employeeName
        },
        filters: {
          ...this.getFilters(),
          employeeId: employee.employeeId
        }
      })
    },

    updateFilters ({ startDate, endDate }) {
      this.filters = { startDate, endDate }
    },

    async getCostCenters () {
      if (this.costCenterStatus === 'loading') return

      this.costCenterStatus = 'loading'

      try {
        const startDate = this.filters.startDate || null
        const endDate = this.filters.endDate || null
        const companyId = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.getCostCenters(
          { orderBy: 'totalFare' },
          { page: 1 },
          { limit: 5 },
          { startDate },
          { endDate },
          { companyId }
        )
        const { series, total } = res.data

        this.statusRides = {
          amount: total.amount,
          totalFare: total.totalFare,
          averageFare: total.averageFare
        }

        this.top5CostCenterItems = series.map(item => {
          return {
            name: item.name,
            value: convertToCurrencyBRL(item.totalFare),
            percent: (item.totalFare / total.totalFare) * 100,
            info: item
          }
        })
        this.costCenterStatus = 'success'
      } catch (err) {
        console.log('Error', err)
        this.costCenterStatus = 'error'
      }
    },

    async getEmployee () {
      if (this.top5EmployeeStatus === 'loading') return

      this.top5EmployeeStatus = 'loading'

      try {
        const startDate = this.filters.startDate || null
        const endDate = this.filters.endDate || null
        const companyId = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.getEmployee(
          { orderBy: 'totalFare' },
          { page: 1 },
          { limit: 5 },
          { startDate },
          { endDate },
          { companyId }
        )

        if (res) {
          const { series, total } = res.data
          this.top5EmployeeItems = series.map(item => {
            return {
              name: item.employeeName,
              value: convertToCurrencyBRL(item.totalFare),
              percent: (item.totalFare / total.totalFare) * 100,
              info: item
            }
          })
        } else {
          this.top5EmployeeItems = []
        }

        this.top5EmployeeStatus = 'success'
      } catch (err) {
        console.log('Error', err)
        this.top5EmployeeStatus = 'error'
      }
    },

    setBgColor () {
      const body = document.querySelector('body')
      const wrap = document.querySelector('.application--wrap')
      if (body) body.style.backgroundColor = '#fff'
      if (wrap) wrap.style.backgroundColor = '#fff'
    },

    removeBgColor () {
      const body = document.querySelector('body')
      const wrap = document.querySelector('.application--wrap')
      if (body) body.style.backgroundColor = 'inherit'
      if (wrap) wrap.style.backgroundColor = 'inherit'
    },

    handleSidebarVisible () {
      this.sidebarVisible = this.claims.report['ROLE_GET_REPORT_TAXIJOBS'] && window.innerWidth >= 1180
    }
  },
  mounted () {
    this.setBgColor()
    this.handleSidebarVisible()
    this.getCostCenters()
    this.getEmployee()
    window.addEventListener('resize', this.handleSidebarVisible)
  },
  beforeDestroy () {
    this.removeBgColor()
    window.removeEventListener('resize', this.handleSidebarVisible)
  },
  watch: {
    filters: {
      deep: true,
      handler () {
        this.getCostCenters()
        this.getEmployee()
        this.closeGlobalDrawer()
      }
    },

    company () {
      this.getCostCenters()
      this.getEmployee()
      this.closeGlobalDrawer()
    }
  }
}
</script>

<style lang="scss" scoped>
.page--home {

  .page-content {

    @media (min-width: 768px) {
      padding: 80px 12px 12px;
    }

    &.page-content--sidebar {
      padding-right: 372px;
    }
  }

  .page-content__body {
    margin: 0 auto;
    max-width: 842px;
  }
}
</style>
